import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import axios from '../../../api/axios';
import { BusinessCategory1, BusinessCategory2, BusinessCategory3, User } from '../../../types';
import { validateEmail, containsEmoji, validatePhoneNumber } from '../../../utils/Utils';
import { ApiError } from '../../../types/errors';
import BusinessCategorySelector from '../../../admin/components/users/BusinessCategorySelector';

interface CompanyDetailsModalProps {
  show: boolean;
  onHide: () => void;
  user: User | null;
}

interface CompanyData {
  name: string;
  email: string;
  contact_person: string;
  phone: string;
  business_category1_id: number;
  business_category2_id: number;
  business_category3_id: number;
}

interface CompanyErrors {
  name?: string;
  email?: string;
  contact_person?: string;
  phone?: string;
  business_category1_id?: string;
  business_category2_id?: string;
  business_category3_id?: string;
  submit?: string;
}

interface CompanyUpdateEvent extends CustomEvent {
  detail: {
    name: string;
    email: string;
    contact_person: string;
    phone: string;
    business_category1_id: number;
    business_category2_id: number;
    business_category3_id: number;
  };
}

const CompanyDetailsModal: React.FC<CompanyDetailsModalProps> = ({ show, onHide, user }) => {
  const [company, setCompany] = useState<CompanyData>({
    name: '',
    email: '',
    contact_person: '',
    phone: '',
    business_category1_id: 0,
    business_category2_id: 0,
    business_category3_id: 0
  });
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [contactPerson, setContactPerson] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [businessCategory1Id, setBusinessCategory1Id] = useState<number>(0);
  const [businessCategory2Id, setBusinessCategory2Id] = useState<number>(0);
  const [businessCategory3Id, setBusinessCategory3Id] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [errors, setErrors] = useState<CompanyErrors>({});
  const [allCategory1Data, setAllCategory1Data] = useState<BusinessCategory1[]>([]);
  const [allCategory2Data, setAllCategory2Data] = useState<BusinessCategory2[]>([]);
  const [allCategory3Data, setAllCategory3Data] = useState<BusinessCategory3[]>([]);
  const [categoryNames, setCategoryNames] = useState<{
    category1?: string;
    category2?: string;
    category3?: string;
  }>({});

  useEffect(() => {
    if (show && user) {
      setCompany({
        name: user.name || '',
        email: user.email || '',
        contact_person: user.contact_person || '',
        phone: user.phone || '',
        business_category1_id: user.business_category1_id || 0,
        business_category2_id: user.business_category2_id || 0,
        business_category3_id: user.business_category3_id || 0
      });
      setName(user.name || '');
      setEmail(user.email || '');
      setContactPerson(user.contact_person || '');
      setPhone(user.phone || '');
      setBusinessCategory1Id(user.business_category1_id || 0);
      setBusinessCategory2Id(user.business_category2_id || 0);
      setBusinessCategory3Id(user.business_category3_id || 0);
      setIsEditing(false);
      setErrors({});

      loadAllCategoryData();
    }
  }, [show, user]);

  useEffect(() => {
    if (user && allCategory1Data.length > 0 && allCategory2Data.length > 0 && allCategory3Data.length > 0) {
      if (user.business_category1_id) {
        loadCategoryNames(user.business_category1_id, user.business_category2_id, user.business_category3_id);
      } else {
        setCategoryNames({});
      }
    }
  }, [user, allCategory1Data, allCategory2Data, allCategory3Data]);

  const loadAllCategoryData = async () => {
    try {
      const token = localStorage.getItem('token');

      const [response1, response2, response3] = await Promise.all([
        axios.get('/business-categories/1', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/business-categories/2', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/business-categories/3', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setAllCategory1Data(response1.data);
      setAllCategory2Data(response2.data);
      setAllCategory3Data(response3.data);
    } catch (error) {
      console.error('業界カテゴリデータの取得に失敗しました:', error);
    } finally {
    }
  };

  const loadCategoryNames = async (category1Id?: number, category2Id?: number, category3Id?: number) => {
    try {
      const names: { category1?: string; category2?: string; category3?: string } = {};

      if (category1Id) {
        const category1 = allCategory1Data.find((cat: BusinessCategory1) => cat.id === category1Id);
        if (category1) names.category1 = category1.name;

        if (category2Id) {
          const category2 = allCategory2Data.find((cat: BusinessCategory2) => cat.business_category1_id === category1Id && cat.id === category2Id);
          if (category2) names.category2 = category2.name;

          if (category3Id) {
            const category3 = allCategory3Data.find((cat: BusinessCategory3) => 
              cat.business_category1_id === category1Id && 
              cat.business_category2_id === category2Id && 
              cat.id === category3Id
            );
            if (category3) names.category3 = category3.name;
          }
        }
      }

      setCategoryNames(names);
    } catch (error) {
      console.error('業界カテゴリ名の取得に失敗しました:', error);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    if (name === 'name') setName(value);
    if (name === 'email') setEmail(value);
    if (name === 'contact_person') setContactPerson(value);
    if (name === 'phone') setPhone(value);
  };

  const validateForm = (): CompanyErrors => {
    const newErrors: CompanyErrors = {};

    if (!name.trim()) newErrors.name = '会社名を入力してください。';
    else if (containsEmoji(name)) newErrors.name = '絵文字は使用できません。';

    if (!email.trim()) newErrors.email = 'メールアドレスを入力してください。';
    else if (!validateEmail(email)) newErrors.email = '不正なメールアドレスです。';
    else if (containsEmoji(email)) newErrors.email = '絵文字は使用できません。';

    if (!contactPerson.trim()) newErrors.contact_person = '担当者名を入力してください。';
    else if (containsEmoji(contactPerson)) newErrors.contact_person = '絵文字は使用できません。';

    if (!phone.trim()) newErrors.phone = '電話番号を入力してください。';
    else if (!validatePhoneNumber(phone)) newErrors.phone = '不正な電話番号です。';
    else if (containsEmoji(phone)) newErrors.phone = '絵文字は使用できません。';

    return newErrors;
  };

  const handleSubmit = async (): Promise<void> => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const updatedCompany = { 
        name: name, 
        email: email,
        contact_person: contactPerson,
        phone: phone,
        business_category1_id: businessCategory1Id || null,
        business_category2_id: businessCategory2Id || null,
        business_category3_id: businessCategory3Id || null
      };
      const response = await axios.put('/company', updatedCompany, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const responseData = response.data;
      setCompany({
        name: responseData.name,
        email: responseData.email,
        contact_person: responseData.contact_person,
        phone: responseData.phone,
        business_category1_id: responseData.business_category1_id || 0,
        business_category2_id: responseData.business_category2_id || 0,
        business_category3_id: responseData.business_category3_id || 0
      });
      setIsEditing(false);
      setErrors({});

      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({
        ...userData,
        name: responseData.name,
        email: responseData.email,
        contact_person: responseData.contact_person,
        phone: responseData.phone,
        business_category1_id: responseData.business_category1_id,
        business_category2_id: responseData.business_category2_id,
        business_category3_id: responseData.business_category3_id
      }));

      const updateEvent = new CustomEvent('companyUpdated', { 
        detail: {
          name: responseData.name,
          email: responseData.email,
          contact_person: responseData.contact_person,
          phone: responseData.phone,
          business_category1_id: responseData.business_category1_id || 0,
          business_category2_id: responseData.business_category2_id || 0,
          business_category3_id: responseData.business_category3_id || 0
        }
      }) as CompanyUpdateEvent;
      window.dispatchEvent(updateEvent);

      onHide();

      alert('企業情報が更新されました');
    } catch (error: unknown) {
      console.error('Error updating company details:', error);
      const axiosError = error as ApiError;
      if (axiosError.response?.data?.errors) {
        setErrors({
          ...axiosError.response.data.errors,
        });
      } else if (axiosError.response?.data?.message) {
        alert(axiosError.response.data.message);
      } else {
        alert('更新中にエラーが発生しました。もう一度お試しください。');
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size='sm'
      centered
      backdrop="static"  // Prevent close when clicking outside
      keyboard={false}   // Optional: Prevent closing with ESC key
    >
      <Modal.Header closeButton>
        <Modal.Title>企業詳細</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isEditing && (
          <div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">会社名</div>
              <div className="candidate-detail-value">{company.name}</div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">メールアドレス</div>
              <div className="candidate-detail-value">{company.email}</div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">担当者名</div>
              <div className="candidate-detail-value">{company.contact_person || '未設定'}</div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">電話番号</div>
              <div className="candidate-detail-value">{company.phone || '未設定'}</div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">業界・業種</div>
              <div className="candidate-detail-value">
                {company.business_category1_id ? (
                  <div>
                    {categoryNames.category1 && <div>{categoryNames.category1}</div>}
                    {categoryNames.category2 && <div>{categoryNames.category2}</div>}
                    {categoryNames.category3 && <div>{categoryNames.category3}</div>}
                  </div>
                ) : '未設定'}
              </div>
            </div>
          </div>
        )}
        {isEditing && (
          <div className='candidate-edit-panel'>
            <Form>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">会社名 <span className="required-mark">*</span></div>
              <div className="candidate-detail-value">
                <Form.Control
                  type="text"
                  name="name"
                  value={name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  isInvalid={!!errors.name}
                  placeholder="会社名"
                />
                {errors.name && (
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">メールアドレス <span className="required-mark">*</span></div>
              <div className="candidate-detail-value">
                <Form.Control
                  type="text"
                  name="email"
                  value={email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  isInvalid={!!errors.email}
                  placeholder="メールアドレス"
                />
                {errors.email && (
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">担当者名 <span className="required-mark">*</span></div>
              <div className="candidate-detail-value">
                <Form.Control
                  type="text"
                  name="contact_person"
                  value={contactPerson}
                  onChange={handleChange}
                  disabled={!isEditing}
                  isInvalid={!!errors.contact_person}
                  placeholder="担当者名"
                />
                {errors.contact_person && (
                  <Form.Control.Feedback type="invalid">
                    {errors.contact_person}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">電話番号 <span className="required-mark">*</span></div>
              <div className="candidate-detail-value">
                <Form.Control
                  type="text"
                  name="phone"
                  value={phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  isInvalid={!!errors.phone}
                  placeholder="電話番号（例：03-1234-5678）"
                />
                {errors.phone && (
                  <Form.Control.Feedback type="invalid">
                    {errors.phone}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>
            <div className="candidate-detail-item">
              <div className="candidate-detail-label">業界・業種</div>
              <div className="candidate-detail-value">
                <BusinessCategorySelector
                  allCategory1Data={allCategory1Data}
                  allCategory2Data={allCategory2Data}
                  allCategory3Data={allCategory3Data}
                  category1Value={businessCategory1Id}
                  category2Value={businessCategory2Id}
                  category3Value={businessCategory3Id}
                  onCategory1Change={(value) => {
                    setBusinessCategory1Id(value);
                    setBusinessCategory2Id(0);
                    setBusinessCategory3Id(0);
                  }}
                  onCategory2Change={(value) => {
                    setBusinessCategory2Id(value);
                    setBusinessCategory3Id(0);
                  }}
                  onCategory3Change={setBusinessCategory3Id}
                  errors={errors}
                />
              </div>
            </div>
            </Form>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isEditing ? (
          <div className="d-flex justify-content-center w-100">
            <Button variant="success" className="me-3" onClick={handleSubmit}>
              保存
            </Button>
            <Button variant="secondary" onClick={() => {
              setIsEditing(false);
              setErrors({});
            }}>
              キャンセル
            </Button>
          </div>
        ) : (
          <Button variant="success" onClick={() => {
            setIsEditing(true);
            setName(company.name);
            setEmail(company.email);
            setContactPerson(company.contact_person);
            setPhone(company.phone);
            setBusinessCategory1Id(company.business_category1_id);
            setBusinessCategory2Id(company.business_category2_id);
            setBusinessCategory3Id(company.business_category3_id);
          }}>
            編集
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CompanyDetailsModal;
